import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { ADMIN_TABS, AdminTabId } from "./adminTabs";
import "./AdminTabBar.scss";

interface TabIndicator {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface AdminTabBarProps {
  activeTab: AdminTabId;
  onTabChange: (tab: AdminTabId) => void;
}

export const AdminTabBar = ({ activeTab, onTabChange }: AdminTabBarProps) => {
  const tabsRef = useRef<HTMLDivElement>(null);
  const tabButtonRefs = useRef<Partial<Record<AdminTabId, HTMLButtonElement>>>({});
  const [indicator, setIndicator] = useState<TabIndicator>({ left: 0, top: 0, width: 0, height: 0 });

  const updateIndicator = useCallback(() => {
    const container = tabsRef.current;
    const activeButton = tabButtonRefs.current[activeTab];
    if (!container || !activeButton) return;

    setIndicator({
      left: activeButton.offsetLeft,
      top: activeButton.offsetTop,
      width: activeButton.offsetWidth,
      height: activeButton.offsetHeight,
    });
  }, [activeTab]);

  useLayoutEffect(() => {
    updateIndicator();
    const activeButton = tabButtonRefs.current[activeTab];
    activeButton?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest",
    });
  }, [updateIndicator, activeTab]);

  useLayoutEffect(() => {
    const container = tabsRef.current;
    if (!container) return;

    const onScroll = () => updateIndicator();
    container.addEventListener("scroll", onScroll, { passive: true });

    const observer = new ResizeObserver(updateIndicator);
    observer.observe(container);
    ADMIN_TABS.forEach((tab) => {
      const button = tabButtonRefs.current[tab.id];
      if (button) observer.observe(button);
    });

    return () => {
      container.removeEventListener("scroll", onScroll);
      observer.disconnect();
    };
  }, [updateIndicator]);

  return (
    <div className="admin-tab-bar" role="tablist" aria-label="Разделы администратора">
      <div className="admin-tab-bar__tabs" ref={tabsRef}>
        {ADMIN_TABS.map((tab) => (
          <button
            key={tab.id}
            ref={(node) => {
              if (node) tabButtonRefs.current[tab.id] = node;
              else delete tabButtonRefs.current[tab.id];
            }}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            className={`admin-tab-bar__tab${activeTab === tab.id ? " admin-tab-bar__tab--active" : ""}`}
            onClick={() => onTabChange(tab.id)}
          >
            {tab.label}
          </button>
        ))}
        <span
          className="admin-tab-bar__indicator"
          style={{
            transform: `translate(${indicator.left}px, ${indicator.top}px)`,
            width: indicator.width,
            height: indicator.height,
          }}
          aria-hidden
        />
      </div>
    </div>
  );
};
